// The two screens that are not the show.
//
// A seat used to look the same at half past six as it did mid-performance: the
// phone connected, drew its spectrum, and waited for somebody to touch it. The
// hall now says which of three states it is in, and this is the other two.
//
//   before  "the show will begin soon", and the same About button that sits at
//           the bottom of the scanner — this is the screen most people will be
//           looking at for twenty minutes, and it is the one place a hall gets
//           to say what they are about to hear.
//   after   nothing at all, with the About button centred. Deliberately empty:
//           the show is over, there is nothing to do, and a screen that keeps
//           offering things is a screen people keep holding.
//
// Built here rather than in index.html, following the entry gate: markup for a
// screen nobody is looking at most of the time is markup that goes stale
// unnoticed. It is a near copy of the scanner's own layout, on purpose — the
// three-quarters hero over a bottom bar is what this app looks like when it is
// not the instrument, and a second shape for the same job would just be a second
// shape to keep in step.
import { t } from "./i18n";

// The states the server sends (SET_SHOW). 1 is the show itself and has no
// screen here — that is the app.
export const SHOW_SOON = 0;
export const SHOW_LIVE = 1;
export const SHOW_ENDED = 2;

const ID = "show-screen";
// Long enough to read as a change of scene rather than a repaint, short enough
// that nobody waits for it. The house fade, same as the scanner's camera.
const FADE_MS = 400;

let el: HTMLElement | null = null;
let shown: number | null = null;
let removeTimer: ReturnType<typeof setTimeout> | undefined;

// The About button, verbatim from the scanner: the same markup, the same two
// strings and the same destination. It is the one thing on this screen that is
// already a known quantity, and a second version of it would only be a second
// thing to keep in step.
function cta(): string {
  return `
    <div id="gate-cta">
      <a id="gate-cta-btn" href="https://cosmicsymphonydeck.com" target="_blank" rel="noopener">
        <span class="gate-cta-glyph" aria-hidden="true"></span>
        <span class="gate-cta-text">
          <span class="gate-cta-main">${t("ctaTitle")}</span>
          <span class="gate-cta-sub">${t("ctaSub")}</span>
        </span>
        <svg class="gate-cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M5 12h14M13 6l6 6-6 6"/>
        </svg>
      </a>
    </div>`;
}

function build(state: number): HTMLElement {
  const div = document.createElement("div");
  div.id = ID;
  // The class is the state, so the stylesheet decides the layout rather than
  // this deciding it twice.
  div.className = state === SHOW_ENDED ? "ss-ended out" : "ss-soon out";
  div.innerHTML = state === SHOW_ENDED
    // Nothing above it. The button is centred by the stylesheet, because there
    // is no hero for it to sit under.
    ? cta()
    : `<div class="ss-hero">
         <p class="ss-title">${t("showSoonTitle")}</p>
         <p class="ss-sub">${t("showSoonSub")}</p>
       </div>${cta()}`;
  return div;
}

/**
 * Put the phone on one of the three screens.
 *
 * Idempotent, and called from a downlink frame that can arrive at any moment —
 * including several times in a row while an operator makes up their mind.
 */
export function setShowScreen(state: number): void {
  if (state === shown) return;
  shown = state;
  clearTimeout(removeTimer);

  if (state === SHOW_LIVE) {
    if (!el) return;
    // Fade out, then take it off the page. `.hidden` is not usable here — it is
    // display:none !important, which no opacity transition survives — so the
    // element is removed on a timer instead, and the timer is cleared above in
    // case the state changes again mid-fade.
    const going = el;
    el = null;
    going.classList.add("out");
    removeTimer = setTimeout(() => going.remove(), FADE_MS);
    return;
  }

  // Swapping between the two non-live screens: the old one goes with the same
  // fade rather than being yanked out from under the new one.
  if (el) {
    const going = el;
    going.classList.add("out");
    setTimeout(() => going.remove(), FADE_MS);
  }
  const next = build(state);
  document.body.appendChild(next);
  el = next;
  // One frame on the page before the class comes off, or the browser has
  // nothing to transition FROM and the screen simply appears.
  requestAnimationFrame(() => requestAnimationFrame(() => next.classList.remove("out")));
}
